import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/components/auth/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { Check, X, Loader2 } from 'lucide-react';

interface CreditRequest {
  id: string;
  user_id: string;
  requested_option: string;
  message: string | null;
  status: string;
  admin_note: string | null;
  created_at: string;
  processed_at: string | null;
  user_email?: string;
}

interface RowState {
  credits: string;
  plan: string;
  expiresAt: string;
  adminNote: string;
}

const REQUEST_LABELS: Record<string, string> = {
  more_credits: 'More credits',
  daily: 'Run for a Day',
  per_lens: 'Starter',
  all_lenses: 'Pro',
  custom: 'Custom / Team',
};

const defaultState = (req: CreditRequest): RowState => ({
  credits: req.requested_option === 'more_credits' ? '100' : req.requested_option === 'daily' ? '200' : '1000',
  plan: req.requested_option === 'more_credits' ? '' : req.requested_option,
  expiresAt: '',
  adminNote: '',
});

export const CreditRequests = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [requests, setRequests] = useState<CreditRequest[]>([]);
  const [rowStates, setRowStates] = useState<Record<string, RowState>>({});
  const [loading, setLoading] = useState(true);
  const [processingId, setProcessingId] = useState<string | null>(null);

  const fetchRequests = async () => {
    setLoading(true);
    const { data: reqs } = await supabase
      .from('credit_requests')
      .select('*')
      .order('created_at', { ascending: false });

    const userIds = Array.from(new Set((reqs ?? []).map((r) => r.user_id)));
    const { data: profiles } = await supabase
      .from('profiles')
      .select('user_id, email')
      .in('user_id', userIds.length ? userIds : ['00000000-0000-0000-0000-000000000000']);

    const emailMap = new Map((profiles ?? []).map((p) => [p.user_id, p.email]));
    const enriched: CreditRequest[] = (reqs ?? []).map((r) => ({ ...r, user_email: emailMap.get(r.user_id) }));
    setRequests(enriched);

    const initialStates: Record<string, RowState> = {};
    for (const r of enriched) {
      if (r.status === 'pending') initialStates[r.id] = defaultState(r);
    }
    setRowStates(initialStates);
    setLoading(false);
  };

  useEffect(() => {
    fetchRequests();
  }, []);

  const updateRow = (id: string, patch: Partial<RowState>) => {
    setRowStates((prev) => ({ ...prev, [id]: { ...prev[id], ...patch } }));
  };

  const upsertAttr = async (userId: string, name: string, value: string) => {
    return supabase
      .from('profile_attributes')
      .upsert({ user_id: userId, attribute_name: name, attribute_value: value }, { onConflict: 'user_id,attribute_name' });
  };

  const handleApprove = async (req: CreditRequest) => {
    const state = rowStates[req.id];
    if (!state || !user) return;
    setProcessingId(req.id);
    try {
      // Read current credits and add
      const { data: existing } = await supabase
        .from('profile_attributes')
        .select('attribute_value')
        .eq('user_id', req.user_id)
        .eq('attribute_name', 'credits')
        .maybeSingle();
      const current = parseInt(existing?.attribute_value ?? '0', 10) || 0;
      const toAdd = parseInt(state.credits, 10) || 0;
      const newTotal = current + toAdd;

      await upsertAttr(req.user_id, 'credits', String(newTotal));

      if (state.plan) {
        await upsertAttr(req.user_id, 'plan', state.plan);
        await upsertAttr(req.user_id, 'plan_start', new Date().toISOString());
        if (state.expiresAt) {
          await upsertAttr(req.user_id, 'plan_end', new Date(state.expiresAt).toISOString());
        }
      }

      const { error: updErr } = await supabase
        .from('credit_requests')
        .update({
          status: 'approved',
          admin_note: state.adminNote || null,
          processed_by: user.id,
          processed_at: new Date().toISOString(),
        })
        .eq('id', req.id);
      if (updErr) throw updErr;

      toast({ title: 'Approved', description: `Added ${toAdd} credits to ${req.user_email}` });
      await fetchRequests();
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Unknown error';
      toast({ title: 'Approval failed', description: message, variant: 'destructive' });
    } finally {
      setProcessingId(null);
    }
  };

  const handleReject = async (req: CreditRequest) => {
    if (!user) return;
    setProcessingId(req.id);
    const state = rowStates[req.id];
    const { error } = await supabase
      .from('credit_requests')
      .update({
        status: 'rejected',
        admin_note: state?.adminNote || null,
        processed_by: user.id,
        processed_at: new Date().toISOString(),
      })
      .eq('id', req.id);
    setProcessingId(null);
    if (error) {
      toast({ title: 'Rejection failed', description: error.message, variant: 'destructive' });
      return;
    }
    toast({ title: 'Rejected' });
    await fetchRequests();
  };

  const pending = requests.filter((r) => r.status === 'pending');
  const processed = requests.filter((r) => r.status !== 'pending');

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-foreground">Credit Requests</h2>
        <p className="text-muted-foreground">Approve or reject user upgrade requests</p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12 text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading…
        </div>
      ) : (
        <>
          <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-3">Pending ({pending.length})</h3>
          <div className="space-y-4 mb-10">
            {pending.length === 0 && (
              <Card><CardContent className="p-6 text-sm text-muted-foreground">No pending requests.</CardContent></Card>
            )}
            {pending.map((req) => {
              const state = rowStates[req.id];
              if (!state) return null;
              return (
                <Card key={req.id}>
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <div className="font-medium">{req.user_email ?? req.user_id}</div>
                        <div className="text-xs text-muted-foreground">
                          Requested: <Badge variant="secondary" className="ml-1">{REQUEST_LABELS[req.requested_option] ?? req.requested_option}</Badge> · {new Date(req.created_at).toLocaleString()}
                        </div>
                        {req.message && (
                          <div className="mt-2 text-sm bg-muted/50 rounded p-2 italic">"{req.message}"</div>
                        )}
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
                      <div>
                        <label className="text-xs text-muted-foreground">Credits to add</label>
                        <Input type="number" value={state.credits} onChange={(e) => updateRow(req.id, { credits: e.target.value })} />
                      </div>
                      <div>
                        <label className="text-xs text-muted-foreground">Set plan</label>
                        <Select value={state.plan || 'no_change'} onValueChange={(v) => updateRow(req.id, { plan: v === 'no_change' ? '' : v })}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="no_change">No change</SelectItem>
                            <SelectItem value="free">free</SelectItem>
                            <SelectItem value="daily">daily</SelectItem>
                            <SelectItem value="monthly">monthly</SelectItem>
                            <SelectItem value="custom">custom</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <label className="text-xs text-muted-foreground">Expires</label>
                        <Input type="date" value={state.expiresAt} onChange={(e) => updateRow(req.id, { expiresAt: e.target.value })} />
                      </div>
                    </div>

                    <Textarea
                      placeholder="Admin note (optional)"
                      value={state.adminNote}
                      onChange={(e) => updateRow(req.id, { adminNote: e.target.value })}
                      rows={2}
                      className="mb-3"
                    />

                    <div className="flex gap-2 justify-end">
                      <Button variant="outline" size="sm" disabled={processingId === req.id} onClick={() => handleReject(req)}>
                        <X className="h-4 w-4 mr-1" /> Reject
                      </Button>
                      <Button size="sm" disabled={processingId === req.id} onClick={() => handleApprove(req)}>
                        {processingId === req.id ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Check className="h-4 w-4 mr-1" />}
                        Approve
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-3">Processed ({processed.length})</h3>
          <div className="space-y-2">
            {processed.map((req) => (
              <Card key={req.id}>
                <CardContent className="p-3 text-sm flex items-center justify-between">
                  <div>
                    <span className="font-medium">{req.user_email ?? req.user_id}</span>
                    <span className="text-muted-foreground ml-2">· {REQUEST_LABELS[req.requested_option] ?? req.requested_option}</span>
                    {req.admin_note && <span className="text-muted-foreground ml-2 italic">"{req.admin_note}"</span>}
                  </div>
                  <Badge variant={req.status === 'approved' ? 'default' : 'destructive'}>{req.status}</Badge>
                </CardContent>
              </Card>
            ))}
          </div>
        </>
      )}
    </div>
  );
};
